import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import type { CollectionResponse } from 'api/market-view-api';
import {
  createProduct,
  getProducts,
  updateProduct,
} from 'api/generated/product-controller/product-controller';
import type { ProductDto } from 'api/generated/schemas/productDto';
import type { ProductRequest } from 'api/generated/schemas/productRequest';
import type { GetProductsParams } from 'api/generated/schemas/getProductsParams';
import { parsePaginationHeaders } from 'helpers/api/parse-pagination-headers';
import { toQueryParamsObject } from 'helpers/api/to-query-params-object';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';
import { useCurrentUser } from 'hooks/api/use-current-user';

export const productsKeys = {
  all: ['products'] as const,
  lists: () => [...productsKeys.all, 'list'] as const,
  list: (params?: ProductsListParams) =>
    [...productsKeys.lists(), params] as const,
};

export interface ProductsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

interface UseProductsListArgs extends ProductsListParams {
  enabled?: boolean;
}

export const useProductsList = ({
  pageSize = 1000,
  pageIndex = 0,
  sortBy = [],
  filters,
  enabled = true,
}: UseProductsListArgs = {}): UseQueryResult<
  CollectionResponse<ProductDto[]>,
  Error
> => {
  const user = useCurrentUser();
  const userId = (user?.data?.oktaUserId ?? '') as string;

  return useQuery({
    queryKey: productsKeys.list({ pageSize, pageIndex, sortBy, filters }),
    queryFn: async () => {
      const response = await getProducts(
        userId,
        toQueryParamsObject({
          pageSize,
          pageIndex,
          sortBy,
          filters,
        }) as GetProductsParams
      );

      return {
        data: response.data,
        pagination: parsePaginationHeaders(response.headers, {
          pageIndex,
          pageSize,
        }),
      };
    },
    enabled: enabled && !!userId,
    placeholderData: (previousData) => previousData,
  });
};

interface CreateProductVariables {
  userId: string;
  productData: ProductRequest;
}

export const useCreateProduct = (): UseMutationResult<
  ProductDto,
  Error,
  CreateProductVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, productData }: CreateProductVariables) =>
      createProduct(userId, productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.lists() });
    },
  });
};

interface ModifyProductVariables {
  userId: string;
  productId: string | number;
  productData: ProductRequest;
}

export const useModifyProduct = (): UseMutationResult<
  ProductDto,
  Error,
  ModifyProductVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, productId, productData }: ModifyProductVariables) =>
      updateProduct(userId, Number(productId), productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productsKeys.lists() });
    },
  });
};
